import React, { PropsWithChildren, useMemo, useState, useEffect } from 'react';

import clsx from 'clsx';
import Image from 'next/image';

import { reminderVisibility, saveData } from '@survey/core/utils/storage';
import { SurveyReminder } from '@survey/core/survey-reminder';

import { ImageProps } from '@typings/image';

import { Nav, NavProps } from '../nav';
import { Footer, FooterProps } from '../footer';

import { RootContext, RootContextType } from './root-context';

import classes from './root.module.scss';

type RawProps = {
  className?: string;
  mainClassName?: string;
  fullscreen?: boolean;
  navOptions?: Omit<NavProps, 'router'>;
  footerOptions?: Pick<FooterProps, 'isHidden' | 'border'>;
  background?: ImageProps;
};

type Props = PropsWithChildren<RawProps>;

export const Root = ({
  className,
  mainClassName,
  fullscreen,
  navOptions = {},
  footerOptions = {},
  children,
  background,
}: Props) => {
  const { backgroundTransparent, minimalMode } = navOptions;

  const [reminderVisible, setReminderVisible] = useState(false);
  const [navVisible, setNavVisibility] = useState(true);

  useEffect(() => {
    if (!navOptions.minimalMode) {
      setReminderVisible(reminderVisibility());
    }
  }, []);

  const contextValue: RootContextType = useMemo(() => {
    return {
      reminderVisible,
      reminderToggle: (visible) => {
        saveData({ reminder: visible });
        setReminderVisible(visible);
      },
      setNavVisibility,
      navVisible,
    };
  }, [reminderVisible]);

  useEffect(() => {
    if (!navOptions.minimalMode) {
      setReminderVisible(reminderVisibility());
    }
  }, []);

  return (
    <RootContext.Provider value={contextValue}>
      <div
        className={clsx(
          classes.root,
          fullscreen && classes.fullscreen,
          className,
        )}
      >
        {navVisible && <Nav {...navOptions} />}
        <SurveyReminder isAllowed={!minimalMode} />
        <main
          className={clsx(classes.root__main, mainClassName, {
            [classes.noMarginTop]: backgroundTransparent,
            [classes.minimalModeMargin]: minimalMode,
            [classes.reminderMargin]: reminderVisible && !backgroundTransparent,
            [classes.reminderMarginNavTransparent]:
              reminderVisible && backgroundTransparent,
            [classes.fullscreen]: fullscreen,
            [classes.noMarginTop]: !navVisible,
            [classes.withBackground]: background,
          })}
        >
          {children}

          {background && (
            <div className={classes.background}>
              <Image // eslint-disable-line
                {...background}
                className={clsx(classes.background, background.className)}
              />
            </div>
          )}
        </main>
        <Footer {...footerOptions} />
      </div>
    </RootContext.Provider>
  );
};
